import React, { useEffect, useState } from "react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import axios, { cookies, MEDIA_BASE_URL } from "../axios/axios";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

const CullingForm = () => {
  const [services, setServices] = useState([]);
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [loading, setLoading] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get("/services/getServices");
        setServices(response.data.services);
      } catch (error) {
        setErrorMessage(error.response?.data?.message || "An error occurred");
        toast.error(error.response?.data?.message || "An error occurred");
      }
    };
    fetchServices();
  }, []);

  const newData = services.find(
    (val) => val.id.replaceAll(" ", "-").replaceAll("/", "_") === id
  );

  const [data, setData] = useState({
    price: "",
    unit_price: "",
    culling_percentage: "",
    labels_flag_rating: "",
    output_format: "",
    quantity: "",
    social_media: "",
    drive_link: "",
    additional_notes: "",
  });

  useEffect(() => {
    if (newData) {
      setData((prevData) => ({
        ...prevData,
        unit_price: Number(newData.price) || 0,
        price: Number(newData.price) || 0,
        quantity: prevData.quantity || "1",
      }));
    }
  }, [newData]);

  const getQuantity = () => Math.max(0, Number(data.quantity) || 0);

  const calculateTotal = (
    qty = getQuantity(),
    unit = Number(data.unit_price) || 0
  ) => {
    const base = Number(unit) * Math.max(1, qty || 1);
    if (isCouponApplied && discount) {
      return Number((base * (1 - discount / 100)).toFixed(2));
    }
    return Number(base.toFixed(2));
  };

  const dataHandler = (e) => {
    const { name, value } = e.target;

    if (name === "quantity") {
      const qty = Math.max(0, Math.floor(Number(value) || 0));

      setData((prevData) => {
        const unit = Number(prevData.unit_price) || 0;
        const newPrice =
          isCouponApplied && discount
            ? Number((unit * qty * (1 - discount / 100)).toFixed(2))
            : Number((unit * qty).toFixed(2));
        return { ...prevData, quantity: qty.toString(), price: newPrice };
      });

      // Trim existing selected files if they exceed new qty
      if (file && file.length > 0) {
        const selectedCount = file.length;
        if (qty === 1 && selectedCount > 1) {
          setFile([file[0]]);
          toast.info("Quantity set to 1 — extra selected files were removed.");
        } else if (qty > 0 && selectedCount > qty) {
          setFile(file.slice(0, qty));
          toast.info(
            `Quantity set to ${qty} — extra selected files were removed.`
          );
        }
      }
    } else {
      setData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const onFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const qty = getQuantity();

    if (qty > 0) {
      if (selectedFiles.length > qty) {
        setFile(selectedFiles.slice(0, qty));
        toast.warn(
          `You selected more than ${qty} files — only the first ${qty} were kept.`
        );
        return;
      }

      if (selectedFiles.length < qty) {
        setFile(selectedFiles);
        toast.info(
          `You selected ${selectedFiles.length} files. Please select exactly ${qty} files before submitting.`
        );
        return;
      }

      setFile(selectedFiles);
    } else {
      setFile(selectedFiles);
      toast.info("Set quantity to enforce exact file count.");
    }
  };

  const applyCoupon = async () => {
    try {
      const storedUser = cookies.get("user");
      console.log("storedUser from cookies:", storedUser);

      const userId = storedUser?.user?.id || null;

      if (!userId) {
        toast.error("User not found. Please log in again.");
        return;
      }

      const response = await axios.post("/coupon/validate", {
        code: couponCode,
        userId,
      });

      if (response.data.valid) {
        setDiscount(response.data.discount);
        setIsCouponApplied(true);

        setData((prevData) => {
          const unit = Number(prevData.unit_price) || 0;
          const qty = getQuantity() || 1;
          const total = Number(
            (unit * qty * (1 - response.data.discount / 100)).toFixed(2)
          );
          return { ...prevData, price: total };
        });

        toast.success("Coupon applied!");
      } else {
        setErrorMessage(response.data.message || "Coupon not valid");
        toast.error(response.data.message || "Coupon not valid");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      setErrorMessage(
        error.response?.data?.message || "Error validating coupon."
      );
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };
  const dataSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: responseData } = await axios.post(`/culling/${id}`, data);

      toast.success("Data Submitted successfully!");
      navigate(`/upload-files/${responseData.id}?type=culling`);
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!newData) {
    return <h2 className="text-center my-5">Loading...</h2>;
  }

  const qty = getQuantity();
  const originalTotal = Number(data.unit_price || 0) * (qty || 1);

  return (
    <>
      <Navbar />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <div className="mx-5">
        <form onSubmit={dataSubmit}>
          <div className="row align-items-center py-5">
            <div className="col-md-6 my-4">
              <img
                className="w-100 object-fit-cover"
                height={600}
                src={`${MEDIA_BASE_URL}/${newData.image}`}
                crossOrigin="anonymous"
                alt=""
              />
            </div>
            <div className="col-md-6 my-4 ps-5">
              <h4 className="mb-3">{newData.title}</h4>
              <h5 className="mb-3">
                Price:{" "}
                {isCouponApplied ? (
                  <>
                    <del className="text-danger me-2">
                      ${originalTotal.toFixed(2)}
                    </del>
                    <span className="text-success">
                      ${Number(data.price).toFixed(2)}
                    </span>
                  </>
                ) : (
                  <span className="text-dark">${originalTotal.toFixed(2)}</span>
                )}
              </h5>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Coupon Code</label>
                </div>
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                    placeholder="Enter Coupon"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    disabled={isCouponApplied}
                  />
                </div>
                <button
                  type="button"
                  className="col-md-2 btn py-2 px-0 rounded-2 btn-secondary mt-2"
                  onClick={applyCoupon}
                  disabled={isCouponApplied}
                >
                  Apply
                </button>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">
                    Culling Percentage
                  </label>
                </div>
                <div className="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="culling_percentage"
                    value={data.culling_percentage}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="Upto 30%">Upto 30%</option>
                    <option value="31% to 50%">31% to 50%</option>
                    <option value="51% to 70%">51% to 70%</option>
                  </select>
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">
                    Keep my labels, flag, and rating
                  </label>
                </div>
                <div className="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="labels_flag_rating"
                    value={data.labels_flag_rating}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="Remove all flags/Starred/Labels from images in my LR catalog">
                      Remove all flags/Starred/Labels from images in my LR
                      catalog
                    </option>
                    <option value="Keep all flags/Starred/Labels + Use collection for culled/B&W images in my LR catalog">
                      Keep all flags/Starred/Labels + Use collection for
                      culled/B&W images in my LR catalog
                    </option>
                  </select>
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Output Format</label>
                </div>
                <div className="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="output_format"
                    value={data.output_format}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="JPEG Image">JPEG Image</option>
                    <option value="Catalog - Lightroom 5">
                      Catalog - Lightroom 5
                    </option>
                    <option value="Catalog - Lightroom 6/CC">
                      Catalog - Lightroom 6/CC
                    </option>
                    <option value="Catalog - Lightroom Classic">
                      Catalog - Lightroom Classic
                    </option>
                    <option value="Catalog - Lightroom 5 & XMPs">
                      Catalog - Lightroom 5 & XMPs
                    </option>
                    <option value="Catalog - Lightroom 6/CC & XMPs">
                      Catalog - Lightroom 6/CC & XMPs
                    </option>
                    <option value="Catalog - Lightroom Classic & XMPs">
                      Catalog - Lightroom Classic & XMPs
                    </option>
                  </select>
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Social Media</label>
                </div>
                <div className="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="social_media"
                    value={data.social_media}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Quantity</label>
                </div>
                <div className="col-md-8">
                  <input
                    onChange={dataHandler}
                    type="number"
                    name="quantity"
                    value={data.quantity}
                    className="form-control shadow-none rounded-2 p-2"
                    min={1}
                  />
                </div>
              </div>

              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">
                    Additional Notes
                  </label>
                </div>
                <div className="col-md-8">
                  <textarea
                    onChange={dataHandler}
                    name="additional_notes"
                    value={data.additional_notes}
                    className="form-control shadow-none rounded-2 p-2"
                  ></textarea>
                </div>
              </div>
              <button
                type="submit"
                className="btn bg_maincolor text-white"
                disabled={loading}
              >
                {loading ? "Loading..." : "Order Now"}
              </button>
            </div>
          </div>
        </form>
      </div>
      <Footer />
    </>
  );
};

export default CullingForm;
